import { CommonList } from "@sscs/types";
import {
  calculatePaginationOffset,
  convertSortToQueryString,
} from "@sscs/utils";
import { INSIGHTS_ENDPOINT } from "Constants";
import { baseApi, listenToGroupUpdates } from "Services";
import { GroupUpdatesMessage } from "Types";
import {
  CommonInsightsListRequest,
  CreateInsightsSearchRequest,
  InsightDetails,
  InsightDetailsRequest,
  InsightVersionsComponentResponse,
  InsightVersionsComponentsRequest,
  InsightVersionsRequest,
  InsightVersionsResponse,
  InsightsResults,
  InsightsSearchRequest,
  InsightsSearchesRequest,
  MyInsightsSearch,
  QuickInsightsSearch,
  SearchInsightsRequest,
  UpdateInsightsSearchRequest,
} from "../types";
import { sortMySearches, sortQuickSearches } from "./utils";
import {
  updateMySearchesCache,
  updateQuickSearchesCache,
} from "./websocketUpdates";

export const insightsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getMySearches: build.query<MyInsightsSearch[], InsightsSearchesRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/list/mysearch`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
      transformResponse(data: MyInsightsSearch[]): MyInsightsSearch[] {
        return sortMySearches(data);
      },
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const listener = (event: MessageEvent) => {
          const message = JSON.parse(event.data) as GroupUpdatesMessage;

          updateCachedData((draft) => {
            return updateMySearchesCache(draft, message);
          });
        };

        await listenToGroupUpdates(
          cacheDataLoaded,
          cacheEntryRemoved,
          listener,
        );
      },
      providesTags: ["InsightsSearches"],
    }),
    createInsightsSearch: build.mutation<
      MyInsightsSearch,
      CreateInsightsSearchRequest
    >({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/create`,
        method: "POST",
        body: queryArg.body,
      }),
      invalidatesTags: ["InsightsSearches"],
    }),
    getInsightsSearch: build.query<MyInsightsSearch, InsightsSearchRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/search/${queryArg.id}`,
      }),
      providesTags: ["InsightsSearches"],
    }),
    updateInsightsSearch: build.mutation<
      MyInsightsSearch,
      UpdateInsightsSearchRequest
    >({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/search/${queryArg.id}`,
        method: "PATCH",
        body: queryArg.body,
      }),
      invalidatesTags: ["InsightsSearches"],
    }),
    deleteInsightsSearch: build.mutation<unknown, InsightsSearchRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/search/${queryArg.id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["InsightsSearches"],
    }),
    getQuickSearches: build.query<
      QuickInsightsSearch[],
      InsightsSearchesRequest
    >({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/list/quicksearch`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const listener = (event: MessageEvent) => {
          const message = JSON.parse(event.data) as GroupUpdatesMessage;

          updateCachedData((draft) => {
            return updateQuickSearchesCache(draft, message);
          });
        };

        await listenToGroupUpdates(
          cacheDataLoaded,
          cacheEntryRemoved,
          listener,
        );
      },
      transformResponse(data: QuickInsightsSearch[]): QuickInsightsSearch[] {
        return sortQuickSearches(data);
      },
    }),
    getInsightsTags: build.query<CommonList, CommonInsightsListRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/component/tag`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getInsightsPackages: build.query<CommonList, CommonInsightsListRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/package`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getInsightsProjects: build.query<CommonList, CommonInsightsListRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/project`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getInsightsIssues: build.query<CommonList, CommonInsightsListRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/violation/identifier`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getInsightsCWEs: build.query<CommonList, CommonInsightsListRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/vulnerability/cwe`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getInsightsMarketingNames: build.query<
      CommonList,
      CommonInsightsListRequest
    >({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/vulnerability/marketing-name`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getInsightsEndpoints: build.query<CommonList, CommonInsightsListRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/secret/endpoint`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getInsightsServices: build.query<CommonList, CommonInsightsListRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/secret/service`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getInsightsSpdxIds: build.query<CommonList, CommonInsightsListRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/license/spdx-id`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getInsightsThreatPlatforms: build.query<
      CommonList,
      CommonInsightsListRequest
    >({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/malware/threat-platform`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getInsightsThreatTypes: build.query<CommonList, CommonInsightsListRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/malware/threat-type`,
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    searchInsights: build.query<InsightsResults, SearchInsightsRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/search`,
        method: "POST",
        body: {
          filters: queryArg.filters,
        },
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getIssueDetails: build.query<InsightDetails, InsightDetailsRequest>({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/${queryArg.type}/${queryArg.id}/details`,
        method: "POST",
        body: {
          filters: queryArg.filters,
        },
        params: {
          group_id: queryArg.groupId,
        },
      }),
    }),
    getIssueVersions: build.query<
      InsightVersionsResponse,
      InsightVersionsRequest
    >({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/${queryArg.type}/${queryArg.id}/versions`,
        method: "POST",
        body: {
          filters: queryArg.filters,
        },
        params: {
          group_id: queryArg.groupId,
          limit: queryArg.pagination.itemsPerPage,
          offset: calculatePaginationOffset(queryArg.pagination),
          sort_by: convertSortToQueryString(queryArg.sort),
        },
      }),
    }),
    getIssueVersionComponents: build.query<
      InsightVersionsComponentResponse,
      InsightVersionsComponentsRequest
    >({
      query: (queryArg) => ({
        url: `${INSIGHTS_ENDPOINT}/${queryArg.type}/${queryArg.id}/versions/${queryArg.reportId}`,
        method: "POST",
        body: {
          filters: queryArg.filters,
        },
        params: {
          group_id: queryArg.groupId,
          limit: queryArg.pagination.itemsPerPage,
          offset: calculatePaginationOffset(queryArg.pagination),
          sort_by: convertSortToQueryString(queryArg.sort),
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMySearchesQuery,
  useCreateInsightsSearchMutation,
  useGetInsightsSearchQuery,
  useUpdateInsightsSearchMutation,
  useDeleteInsightsSearchMutation,
  useGetQuickSearchesQuery,
  useSearchInsightsQuery,
  useGetIssueDetailsQuery,
  useGetIssueVersionsQuery,
  useGetIssueVersionComponentsQuery,
  useLazyGetInsightsCWEsQuery,
  useLazyGetInsightsEndpointsQuery,
  useLazyGetInsightsIssuesQuery,
  useLazyGetInsightsMarketingNamesQuery,
  useLazyGetInsightsPackagesQuery,
  useLazyGetInsightsProjectsQuery,
  useLazyGetInsightsServicesQuery,
  useLazyGetInsightsSpdxIdsQuery,
  useLazyGetInsightsTagsQuery,
  useLazyGetInsightsThreatPlatformsQuery,
  useLazyGetInsightsThreatTypesQuery,
} = insightsApi;
